import { PvpFlag, SwitchNote, TICK_MS, flagStateOf, pvpUnlocked } from "odyssey-shared";
import { say } from "../../words.ts";
import { PAGE_RULES } from "../rules.ts";

/**
 * The flag toggle's panel as the page reads it (ADR-076 decision 6; the design's `flag-toggle` board in
 * `ui_kits/surfaces/PvpSurfaces.jsx`): which of the design's states the panel is in, the line it shows, whether its
 * button is locked and what it asks for, and the countdown's progress, from what the server last said (`pvpState`,
 * `pvpSwitch`, `vitals` and the flag byte) and the page clock. The page never decides: the button only asks, and the
 * panel counts only a switch the server says counts. The rules live here apart from the component, so they are tested
 * without React.
 */

/**
 * The keys the hint row under the panel's button names (ADR-076's amendment of 2026-10-01 and its note of
 * 2026-10-03): the key the PvP flag has before a player chooses another, Escape and Enter. Each hint's words are the
 * language files' (`hud.flag.hints.*`); `flagHints` picks the pairs a state shows.
 */
export const FLAG_HINT_KEYS = { panelKey: "U", escapeKey: "Esc", enterKey: "Enter" } as const;

/**
 * The key of the panel's line for each interruption `pvpSwitch` can note.
 */
const INTERRUPTIONS: ReadonlyMap<number, "hud.flag.interruptedCombat" | "hud.flag.interruptedHostile" | "hud.flag.interruptedUnsafe"> = new Map([
  [SwitchNote.InterruptedCombat, "hud.flag.interruptedCombat"],
  [SwitchNote.InterruptedHostile, "hud.flag.interruptedHostile"],
  [SwitchNote.InterruptedUnsafe, "hud.flag.interruptedUnsafe"],
]);

/**
 * The design's states the panel can be in, and three it adds: locked in combat short of Combatant, dead, and with Flag
 * on outside a safe zone (`unsafe`, ADR-076's amendment of 2026-10-01).
 */
export type ToggleState = "protected-idle" | "to-flagged" | "interrupted" | "flagged-idle" | "to-protected" | "locked" | "combatant" | "combat" | "dead" | "unsafe";

/**
 * The player's switch as the page last heard it: when it ends on the page clock (null when none counts), the
 * preference it counts toward, the note of the last refusal or interruption, and whether a switch started now would
 * complete at once, as the server last said.
 */
export interface HudSwitch {
  readonly endsAt: number | null;
  readonly toward: number;
  readonly note: number;
  readonly instant?: boolean;
}

/**
 * What the panel reads: the page clock, the player's flag byte, its preference, when its Combatant window ends (null
 * for none), whether the last `vitals` said in combat and dead, and its switch. `lockedReason` is a sentence for the
 * status line when something other than the server's flag byte bars a switch (the level lock, `levelLockReason`, and a
 * seat in a party, `flagLockReason`); the panel then reads locked with that sentence as its line and the button's title.
 */
export interface ToggleInput {
  readonly nowMs: number;
  readonly flag: number;
  readonly preference: number;
  readonly combatantEndsAt: number | null;
  readonly inCombat: boolean;
  readonly dead: boolean;
  readonly pvpSwitch: HudSwitch;
  readonly lockedReason?: string | null;
}

/**
 * The panel at a moment: its state, its line, whether the line is a warning, whether the button is locked (drawn with
 * the lock glyph and disabled), the button's label and the preference it asks for, the countdown's progress in whole
 * seconds (null when none counts), and how many milliseconds until the view next changes (null when only the server
 * changes it).
 */
export interface ToggleView {
  readonly state: ToggleState;
  readonly line: string;
  readonly warning: boolean;
  readonly locked: boolean;
  readonly button: string;
  readonly wanted: number;
  readonly progress: { readonly value: number; readonly max: number } | null;
  readonly changesInMs: number | null;
}

/**
 * The switch's delays in whole seconds, from the rules' `flag_on` and `flag_off`, and `flag_on_safe` and `flag_off_safe`,
 * the delays inside a safe zone out of combat (ADR-076's amendment of 2026-10-07).
 */
export interface ToggleDelays {
  readonly flagOn: number;
  readonly flagOff: number;
  readonly flagOnSafe: number;
  readonly flagOffSafe: number;
}

/**
 * The delays a rules table's constants give.
 *
 * @param constants The rules' constants, in ticks.
 * @param constants.flag_on Ticks toward Flag on.
 * @param constants.flag_off Ticks toward Protected.
 * @param constants.flag_on_safe Ticks toward Flag on inside a safe zone, out of combat.
 * @param constants.flag_off_safe Ticks toward Protected inside a safe zone, out of combat.
 * @returns The delays in whole seconds.
 * @example
 * toggleDelays({ flag_on: 100, flag_off: 100, flag_on_safe: 0, flag_off_safe: 0 });
 * // { flagOn: 5, flagOff: 5, flagOnSafe: 0, flagOffSafe: 0 }
 */
export function toggleDelays(constants: { readonly flag_on: number; readonly flag_off: number; readonly flag_on_safe: number; readonly flag_off_safe: number }): ToggleDelays {
  const seconds = (ticks: number): number => Math.round((ticks * TICK_MS) / 1000);
  return { flagOn: seconds(constants.flag_on), flagOff: seconds(constants.flag_off), flagOnSafe: seconds(constants.flag_on_safe), flagOffSafe: seconds(constants.flag_off_safe) };
}

/**
 * The idle line of the button that switches to Protected. Where the server says a switch started now would complete at
 * once (`instant`: a safe zone, out of combat) and `flag_off_safe` is 0, it reads "Protected: Instant"; where it says so
 * and the table holds a delay there, that delay; otherwise the delay outside a safe zone.
 *
 * @param delays The switch's delays in whole seconds.
 * @param instant Whether the server said a switch would be instant now.
 * @returns The line.
 */
function protectedIdleLine(delays: ToggleDelays, instant: boolean): string {
  if (instant && delays.flagOffSafe === 0) {
    return say("hud.flag.protectedInstant");
  }
  return say("hud.flag.protectedIn", { seconds: String(instant ? delays.flagOffSafe : delays.flagOff) });
}

/**
 * The idle line of the button that switches to Flag on, worded as `protectedIdleLine` is.
 *
 * @param delays The switch's delays in whole seconds.
 * @param instant Whether the server said a switch would be instant now.
 * @returns The line.
 */
function flaggedIdleLine(delays: ToggleDelays, instant: boolean): string {
  if (instant && delays.flagOnSafe === 0) {
    return say("hud.flag.flaggedInstant");
  }
  return say("hud.flag.flaggedIn", { seconds: String(instant ? delays.flagOnSafe : delays.flagOn) });
}

/**
 * Whole seconds left before a moment, never below 1 while the moment has not been heard to pass, so a countdown never
 * reads 0 before the server says it ended.
 *
 * @param endsAt The moment on the page clock.
 * @param nowMs The page clock.
 * @returns The seconds, rounded up, at least 1.
 */
function secondsLeft(endsAt: number, nowMs: number): number {
  return Math.max(1, Math.ceil((endsAt - nowMs) / 1000));
}

/**
 * Milliseconds until the seconds left next change.
 *
 * @param endsAt The moment on the page clock.
 * @param nowMs The page clock.
 * @returns The wait, or null once the count has reached 1 and holds.
 */
function nextSecond(endsAt: number, nowMs: number): number | null {
  const left = endsAt - nowMs;
  if (left <= 1000) {
    return null;
  }
  const into = left % 1000;
  return into === 0 ? 1000 : into;
}

/**
 * The panel at a moment (ADR-076 decision 6). A counting switch shows its countdown and Cancel. Otherwise the first of
 * these holds: dead; locked in a Hostile zone; seated in a party, the line saying it cannot flag (a Protected party) or
 * unflag (a Flagged one), the server's order; with Flag on, Combatant (its window's seconds left), in combat, or
 * outside a safe zone; a switch just interrupted, naming its cause; and idle at the preference, with the switch's full
 * delay; outside a safe zone a switch just interrupted by leaving one names that instead. The page has no zone grid: with Flag on, a byte that is neither Combatant nor zone-locked rests Protected only
 * on a cell that bars PvP, so a Flagged byte says the player stands outside a safe zone (ADR-076's amendment of
 * 2026-10-01, item 7), and so does a Protected byte with the switching bit, which with Flag on only a safe passage
 * holds (ADR-076's amendment of 2026-10-03); the order follows the server's refusals.
 *
 * @param input What the server last said and the page clock.
 * @param delays The switch's delays in whole seconds.
 * @returns The view.
 * @example
 * flagToggleView({ nowMs: 0, flag: PvpFlag.Protected, preference: PvpFlag.Protected, combatantEndsAt: null, inCombat: false, dead: false, pvpSwitch: { endsAt: 3_000, toward: PvpFlag.Flagged, note: 0 } }, { flagOn: 5, flagOff: 60 });
 * // { state: "to-flagged", line: "Flagged in 3 s", button: "Cancel", wanted: PvpFlag.Protected, progress: { value: 2, max: 5 }, ... }
 */
export function flagToggleView(input: ToggleInput, delays: ToggleDelays): ToggleView {
  const { nowMs, flag, preference, pvpSwitch } = input;
  const flagOn = preference === PvpFlag.Flagged;
  const other = flagOn ? PvpFlag.Protected : PvpFlag.Flagged;
  const idleButton = flagOn ? say("hud.flag.toProtected") : say("hud.flag.toFlagged");
  if (pvpSwitch.endsAt !== null) {
    const toFlagged = pvpSwitch.toward === PvpFlag.Flagged;
    const seconds = secondsLeft(pvpSwitch.endsAt, nowMs);
    const max = toFlagged ? delays.flagOn : delays.flagOff;
    return {
      state: toFlagged ? "to-flagged" : "to-protected",
      line: toFlagged ? say("hud.flag.flaggedIn", { seconds: String(seconds) }) : say("hud.flag.protectedIn", { seconds: String(seconds) }),
      warning: false,
      locked: false,
      button: say("hud.flag.cancel"),
      wanted: preference,
      progress: { value: Math.max(0, max - seconds), max },
      changesInMs: nextSecond(pvpSwitch.endsAt, nowMs),
    };
  }
  const idle = { warning: false, button: idleButton, wanted: other, progress: null, changesInMs: null };
  if (input.dead) {
    return { ...idle, state: "dead", line: say("hud.flag.dead"), locked: true };
  }
  if ((flag & PvpFlag.ZoneLocked) !== 0) {
    return { ...idle, state: "locked", line: say("hud.flag.locked"), locked: true };
  }
  if (input.lockedReason !== undefined && input.lockedReason !== null) {
    return { ...idle, state: "locked", line: input.lockedReason, locked: true };
  }
  if (flagOn && flagStateOf(flag) === PvpFlag.Combatant && input.combatantEndsAt !== null) {
    return { ...idle, state: "combatant", line: say("hud.flag.combatant", { seconds: String(secondsLeft(input.combatantEndsAt, nowMs)) }), locked: true, changesInMs: nextSecond(input.combatantEndsAt, nowMs) };
  }
  if (flagOn && (input.inCombat || flagStateOf(flag) === PvpFlag.Combatant)) {
    return { ...idle, state: "combat", line: say("hud.flag.combat"), locked: true };
  }
  if (flagOn && (flagStateOf(flag) !== PvpFlag.Protected || (flag & PvpFlag.Switching) !== 0)) {
    const left = pvpSwitch.note === SwitchNote.InterruptedUnsafe;
    return { ...idle, state: "unsafe", line: left ? say("hud.flag.interruptedUnsafe") : say("hud.flag.unsafe"), warning: left, locked: true };
  }
  const interruption = INTERRUPTIONS.get(pvpSwitch.note);
  if (interruption !== undefined) {
    return { ...idle, state: "interrupted", line: say(interruption), warning: true, locked: false };
  }
  return { ...idle, state: flagOn ? "flagged-idle" : "protected-idle", line: flagOn ? protectedIdleLine(delays, pvpSwitch.instant === true) : flaggedIdleLine(delays, pvpSwitch.instant === true), locked: false };
}

/**
 * The sentence the panel shows while the character's level bars flagging (ADR-076's amendment of 2026-10-08, C1 to
 * C3): the rules' `pvp_level` named, or null when the level reaches it or is not known yet (the server refuses
 * whatever the page draws).
 *
 * @param level The character's level, or null before it is known.
 * @returns The sentence, or null.
 * @example
 * levelLockReason(1);
 * // "Cannot flag until level 5"
 */
export function levelLockReason(level: number | null): string | null {
  const required = PAGE_RULES.constants.pvp_level;
  return level === null || pvpUnlocked(level, required) ? null : say("hud.flag.levelLocked", { level: String(required) });
}

/**
 * The sentence the panel shows while something other than the flag byte bars a switch (ADR-076's amendment of 2026-10-08,
 * E2 and C3): a seat in a party, whichever preference the character holds, saying it cannot flag in a Protected party or
 * unflag in a Flagged one, the server's order putting the party before the level; else the level lock for a character
 * that holds Protected, a character already Flagged being barred by no level.
 *
 * @param lock What bars a switch: the character's level (null before it is known), whether it is seated in a party and
 *   the preference it holds.
 * @param lock.level The character's level, or null.
 * @param lock.inParty True while the character is seated in a party of two or more.
 * @param lock.preference `PvpFlag.Protected` or `PvpFlag.Flagged`.
 * @returns The sentence, or null when nothing bars a switch.
 * @example
 * flagLockReason({ level: 12, inParty: true, preference: PvpFlag.Protected });
 * // "Cannot flag while in a Protected party"
 */
export function flagLockReason(lock: { readonly level: number | null; readonly inParty: boolean; readonly preference: number }): string | null {
  if (lock.inParty) {
    return lock.preference === PvpFlag.Flagged ? say("hud.flag.partyFlagged") : say("hud.flag.partyProtected");
  }
  return lock.preference === PvpFlag.Flagged ? null : levelLockReason(lock.level);
}

/**
 * What the panel's button does at a moment, for a key that presses it (ADR-076's amendment of 2026-10-01): the
 * preference it asks for, or null while it is locked.
 *
 * @param input What the server last said and the page clock.
 * @param delays The switch's delays in whole seconds.
 * @returns The preference the button asks for, or null.
 */
export function flagPanelAction(input: ToggleInput, delays: ToggleDelays): number | null {
  const view = flagToggleView(input, delays);
  return view.locked ? null : view.wanted;
}

/**
 * The message line's sentence for a note the server sent, when it is a refusal.
 *
 * @param note The `pvpSwitch` note.
 * @param wanted The preference the page last asked for.
 * @returns The sentence, or null for a note that is not a refusal.
 */
export function flagRefusalLine(note: number, wanted: number): string | null {
  if (note === SwitchNote.RefusedCombat) {
    return say("hud.flag.refused.combat");
  }
  if (note === SwitchNote.RefusedHostile) {
    return wanted === PvpFlag.Flagged ? say("hud.flag.refused.hostileFlagged") : say("hud.flag.refused.hostileProtected");
  }
  if (note === SwitchNote.RefusedDead) {
    return say("hud.flag.refused.dead");
  }
  if (note === SwitchNote.RefusedUnsafe) {
    return say("hud.flag.refused.unsafe");
  }
  if (note === SwitchNote.RefusedParty) {
    return wanted === PvpFlag.Flagged ? say("hud.flag.refused.partyProtected") : say("hud.flag.refused.partyFlagged");
  }
  if (note === SwitchNote.RefusedLevel) {
    return say("hud.flag.refused.level", { level: String(PAGE_RULES.constants.pvp_level) });
  }
  return null;
}

/**
 * What the U key does in an open panel (ADR-076's note of 2026-10-03): the switch the button offers, or null while a
 * switch counts or the button is locked, when U only closes the panel. So a second U never cancels a switch; Cancel is
 * the button, or Enter in a panel U opened (`flagPanelAction`).
 *
 * @param input What the server last said and the page clock.
 * @param delays The switch's delays in whole seconds.
 * @returns The preference to ask for, or null.
 * @example
 * flagPanelKeyAction({ ...input, pvpSwitch: { endsAt: 5_000, toward: PvpFlag.Flagged, note: 0 } }, delays);
 * // null
 */
export function flagPanelKeyAction(input: ToggleInput, delays: ToggleDelays): number | null {
  return input.pvpSwitch.endsAt === null ? flagPanelAction(input, delays) : null;
}

/**
 * One key hint: the key glyph's text and the words after it.
 */
export interface FlagHint {
  readonly key: string;
  readonly words: string;
}

/**
 * The key hint row for a panel's state (ADR-076's note of 2026-10-03): where the button offers a switch, U to switch
 * and Esc to close; while a switch counts, Enter to cancel when Enter is live (a panel U opened) and U to close; while
 * the button is locked, U to close.
 *
 * @param view The panel at a moment.
 * @param enterLive Whether Enter presses the button, true only in a panel a key opened.
 * @param panelKey The label of the key the PvP flag is bound to (`controls/`); U unless given.
 * @returns The hints, in the row's order.
 * @example
 * flagHints(flagToggleView(input, delays), true);
 * // [{ key: "U", words: "to switch" }, { key: "Esc", words: "to close" }]
 */
export function flagHints(view: ToggleView, enterLive: boolean, panelKey: string = FLAG_HINT_KEYS.panelKey): readonly FlagHint[] {
  const close = { key: panelKey, words: say("hud.flag.hints.close") };
  if (view.progress !== null) {
    return enterLive ? [{ key: FLAG_HINT_KEYS.enterKey, words: say("hud.flag.hints.cancel") }, close] : [close];
  }
  if (view.locked) {
    return [close];
  }
  return [
    { key: panelKey, words: say("hud.flag.hints.switch") },
    { key: FLAG_HINT_KEYS.escapeKey, words: say("hud.flag.hints.close") },
  ];
}

/**
 * The switch's countdown in the player card's header (ADR-076's note of 2026-10-03): while a switch counts, the
 * panel's line for its seconds left ("Flagged in 4 s", "Protected in 4 s"), holding at 1 until the server says it
 * ended, and when that line next changes; null while nothing counts. It shows whether or not the panel is open, and
 * does not depend on the flag byte's switching bit, which a switch that leaves the mark as it is never sets.
 *
 * @param pvpSwitch The player's switch as the page last heard it.
 * @param nowMs The page clock.
 * @returns The line and the milliseconds until it changes (null once it holds), or null.
 * @example
 * switchCountdown({ endsAt: 5_000, toward: PvpFlag.Flagged, note: 0 }, 1_000);
 * // { line: "Flagged in 4 s", changesInMs: 1000 }
 */
export function switchCountdown(pvpSwitch: HudSwitch, nowMs: number): { readonly line: string; readonly changesInMs: number | null } | null {
  if (pvpSwitch.endsAt === null) {
    return null;
  }
  const seconds = secondsLeft(pvpSwitch.endsAt, nowMs);
  return { line: pvpSwitch.toward === PvpFlag.Flagged ? say("hud.flag.flaggedIn", { seconds: String(seconds) }) : say("hud.flag.protectedIn", { seconds: String(seconds) }), changesInMs: nextSecond(pvpSwitch.endsAt, nowMs) };
}
